import React from 'react';
import styled from 'styled-components';

import HeroSlider from '../../components/HeroSlider';
import SearchWidget from '../../components/home/SearchWidget';
import CategoryBanner from '../../components/home/CategoryBanner';
import ListingsSlider from '../../components/home/ListingsSlider';
import InvestSlider from '../../components/home/InvestSlider';
import DistrictsSlider from '../../components/home/DistrictsSlider';
import PartnersSlider from '../../components/home/PartnersSlider';
import AwardsSlider from '../../components/AwardsSlider';
import ReviewsSlider from '../../components/ReviewsSlider';
import QuickConsult from '../../components/QuickConsult';
import ContactsSection from '../../components/ContactsSection';
import CollectionsSlider from '../../components/home/CollectionsSlider';
import PdfSlider from '../../components/home/PdfSlider';
import HorizontalScroller from '../../components/home/HorizontalScroller';
import TextIntroBlock from '../../components/home/TextIntroBlock';
import ChairmanSection from '../../components/ChairmanSection';
import { normalizeMediaUrl } from '../../api/base';
import { media } from '../../theme/media';
import { BP } from '../../theme/breakpoints';

const Page = styled.main`
  padding: clamp(12px, 2.8vw, 18px) 0;
  display: grid;
  row-gap: clamp(20px, 4vw, 32px);
`;

const Section = styled.section`
  display: grid;
  gap: clamp(12px, 3vw, 16px);
  justify-items: center;
  text-align: left;
`;

const FullWidthSection = styled(Section)`
  justify-items: center;
  text-align: center;      // заголовок по центру
`;

const Container = styled.div`
  width: min(1400px, 100vw);
  margin: 0 auto;
  padding-left: max(clamp(12px, 4vw, 18px), env(safe-area-inset-left));
  padding-right: max(clamp(12px, 4vw, 18px), env(safe-area-inset-right));
`;

const HeroContainer = styled.div`
  width: min(1400px, 100vw);
  margin: 0 auto;
  padding-left: max(clamp(12px, 4vw, 18px), env(safe-area-inset-left));
  padding-right: max(clamp(12px, 4vw, 18px), env(safe-area-inset-right));
`;

const WideContainer = HeroContainer;

const H2 = styled.h2`
  margin: 0;
  font-size: clamp(18px, 4.6vw, 28px);
  line-height: 1.2;
`;

const Sub = styled.p`
  margin: -6px 0 0;
  opacity: 0.8;
  font-size: clamp(13px, 3.6vw, 16px);
`;

const PostCard = styled.a`
  display: flex;
  flex-direction: column;
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--shadow);
  text-decoration: none;
  color: inherit;
  height: 100%;

  img {
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
    flex-shrink: 0;
  }

  h4 {
    margin: 10px 12px 8px;
    color: #000;
    background: var(--gold);
    opacity: 0.92;
    padding: 8px 12px;
    border-radius: 12px;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    width: calc(100% - 24px);
    
    /* Фиксированная высота для одинакового размера карточек */
    min-height: calc(1.4em * 4 + 16px); /* 4 строки + padding */
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }

  .meta {
    display: flex;
    gap: 10px;
    margin: 6px 12px 14px;
    opacity: 0.88;
    font-size: 13px;
    color: #000;
    background: var(--gold);
    padding: 6px 10px;
    border-radius: 10px;
    backdrop-filter: blur(6px);
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    width: calc(100% - 24px);
    flex-shrink: 0;
    
    /* Обрезка длинных тегов */
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export default function HomeView({
  t,
  heroSlidesVisible,
  recommendedVisible,
  collectionsVisible,
  investVisible,
  districtsVisible,
  partnersVisible,
  objectsVisible,
  objectsTitle,
  pdfVisible,
  collectionsWithCoversOnly,
  mediaVisible,
  newsVisible,
  blogVisible,
  awardsVisible,
  perDesktop,
  perDesktop3,
  err,
}) {
  return (
    <Page>
      {!!heroSlidesVisible.length && (
        <HeroContainer>
          <HeroSlider items={heroSlidesVisible} autoplay autoplayIntervalMs={4000} pauseOnHover={false} />
        </HeroContainer>
      )}

      {/* Поисковик */}
      <WideContainer>
        <SearchWidget />
      </WideContainer>

      {/* Банер категорий */}
      <WideContainer>
        <CategoryBanner />
      </WideContainer>

      {!!recommendedVisible.length && (
        <WideContainer>
          <Section aria-label="Рекомендуем">
            <H2>{t('home.weekly', 'Рекомендуем')}</H2>
            <ListingsSlider
              items={recommendedVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={200}
            />
          </Section>
        </WideContainer>
      )}

      {!!investVisible.length && (
        <WideContainer>
          <Section aria-label="Инвестиционная недвижимость">
            <H2>{t('home.invest', 'Инвестиционная недвижимость')}</H2>
            <InvestSlider
              items={investVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={200}
            />
          </Section>
        </WideContainer>
      )}

      {!!collectionsVisible.length && (
        <WideContainer>
          <Section aria-label="Подборки">
            <H2>{t('home.collections', 'Подборки')}</H2>
            <CollectionsSlider
              items={collectionsVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
            />
          </Section>
        </WideContainer>
      )}

      {!!districtsVisible.length && (
        <Container>
          <Section aria-label="Районы">
            <H2>{t('home.districts', 'Районы')}</H2>
            <DistrictsSlider
              items={districtsVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={200}
            />
          </Section>
        </Container>
      )}

      {!!partnersVisible.length && (
        <Container>
          <Section aria-label="Партнёры">
            <H2>{t('home.partners', 'Партнёры')}</H2>
            <PartnersSlider
              items={partnersVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={200}
            />
          </Section>
        </Container>
      )}

      {!!objectsVisible.length && (
        <WideContainer>
          <Section aria-label="Объекты">
            <H2>{objectsTitle}</H2>
            <ListingsSlider
              items={objectsVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={200}
            />
          </Section>
        </WideContainer>
      )}

      {!!pdfVisible.length && (
        <WideContainer>
          <Section>
            <H2>{t('home.authorsTitle', 'Авторские каталоги')}</H2>
            <Sub>{t('home.authorsSubtitle', 'Актуальные PDF-каталоги')}</Sub>
            <PdfSlider
              items={pdfVisible}
              perViewDesktop={perDesktop}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={180}
            />
          </Section>
        </WideContainer>
      )}

      <WideContainer>
        <Section aria-label={t('home.about', 'О компании')}>
          <TextIntroBlock />
        </Section>
      </WideContainer>

      {!!blogVisible.length && (
        <WideContainer>
          <Section>
            <H2>{t('home.blogTitle', 'Блог')}</H2>
            <HorizontalScroller
              perViewDesktop={perDesktop3}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={260}
              itemMax={330}
              maxWidth={1400}
              centered
              pad={8}
              gap={12}
              limitDesktopWidth={false}
            >
              {blogVisible.map((p, i) => (
                <div key={p.id ?? i} style={{ width: 'var(--col-width)' }}>
                  <PostCard href={`/blog/${encodeURIComponent(p.slug)}`}>
                    <img src={normalizeMediaUrl(p.cover)} alt={p.title} loading="lazy" decoding="async" />
                    <h4>{p.title}</h4>
                    <div className="meta">
                      {p.date && <span>{p.date}</span>}
                      {p.tag && <span>#{p.tag}</span>}
                    </div>
                  </PostCard>
                </div>
              ))}
            </HorizontalScroller>
          </Section>
        </WideContainer>
      )}

      {!!mediaVisible.length && (
        <WideContainer>
          <Section>
            <H2>{t('home.mediaTitle')}</H2>
            <HorizontalScroller
              perViewDesktop={perDesktop3}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={260}
              itemMax={330}
              maxWidth={1400}
              centered
              pad={8}
              gap={12}
              limitDesktopWidth={false}
            >
              {mediaVisible.map((p, i) => (
                <div key={p.id ?? i} style={{ width: 'var(--col-width)' }}>
                  <PostCard href={`/socialmedia/${encodeURIComponent(p.slug)}`}>
                    <img src={normalizeMediaUrl(p.cover)} alt={p.title} loading="lazy" decoding="async" />
                    <h4>{p.title}</h4>
                    <div className="meta">
                      {p.date && <span>{p.date}</span>}
                      {p.tag && <span>#{p.tag}</span>}
                    </div>
                  </PostCard>
                </div>
              ))}
            </HorizontalScroller>
          </Section>
        </WideContainer>
      )}

      {!!newsVisible.length && (
        <WideContainer>
          <Section>
            <H2>{t('home.newsTitle')}</H2>
            <HorizontalScroller
              perViewDesktop={perDesktop3}
              perViewTablet={2}
              perViewMobile={1}
              perViewTiny={1}
              itemMin={260}
              itemMax={330}
              maxWidth={1400}
              centered
              pad={8}
              gap={12}
              limitDesktopWidth={false}
            >
              {newsVisible.map((p, i) => (
                <div key={p.id ?? i} style={{ width: 'var(--col-width)' }}>
                  <PostCard href={`/news/${encodeURIComponent(p.slug)}`}>
                    <img src={normalizeMediaUrl(p.cover)} alt={p.title} loading="lazy" decoding="async" />
                    <h4>{p.title}</h4>
                    <div className="meta">
                      {p.date && <span>{p.date}</span>}
                      {p.tag && <span>#{p.tag}</span>}
                    </div>
                  </PostCard>
                </div>
              ))}
            </HorizontalScroller>
          </Section>
        </WideContainer>
      )}

      <WideContainer>
        <Section aria-label={t('home.leadership', 'Руководство')}>
          <ChairmanSection />
        </Section>
      </WideContainer>

      {!!awardsVisible.length && (
        <WideContainer>
          <Section>
            <H2>{t('home.awardsTitle', 'Награды')}</H2>
            <AwardsSlider
              items={awardsVisible}
              perViewDesktop={perDesktop} // 4 (или 2, если хедер схлопнулся)
              perViewTablet={2}
              perViewMobile={2}
              perViewTiny={1}
              itemMin={220}
              itemMax={330}
            />
          </Section>
        </WideContainer>
      )}

      <WideContainer>
        <FullWidthSection>
          <H2>{t('home.reviewsTitle', 'Отзывы')}</H2>
           <ReviewsSlider
              perViewDesktop={3}   // десктоп — три (можно 2, если так красивее)
                 perViewTablet={2}    // планшет — две
                 perViewTiny={1}      // мобильный — одна
                 bpTiny={BP.tiny}     // 640px
                 bpTablet={BP.tablet} // 980px
                 maxWidth="min(1400px, 96vw)" // рельса центрируется под ширину страницы
            />
        </FullWidthSection>
      </WideContainer>

      <WideContainer>
        <FullWidthSection>
          <H2>{t('home.contactTitle', 'Свяжитесь с нами')}</H2>
          <QuickConsult />
        </FullWidthSection>
      </WideContainer>

      <ContactsSection />

      <Container>{err && <div style={{ color: '#c62828' }}>{err}</div>}</Container>
    </Page>
  );
}

